import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import DataTable from "./DataTable";
import InfoTip from "./InfoTip";
import { fmtAed } from "./KpiCard";
import { allColumns, frameIndexMap, populatedColumns } from "../lib/columns";
import { normaliseNeedle, rowMatchesText } from "../lib/textSearch";
import { useScrollEdges } from "../lib/scrollEdges";
import type { FrameRecords, SideDisplayConfig, UnreconciledBlock } from "../types/api";

interface Props {
  block: UnreconciledBlock;
  insurerName: string;
  // This period's frames, read ONLY to show the transaction that settled an item — never
  // mutated (that is what would move insurer_closing and the Difference).
  criFrame?: FrameRecords;
  insurerFrame?: FrameRecords;
  // Curated display columns per side, so a settling row reads like every other grid in
  // the app rather than exposing raw engine column names.
  criConfig?: SideDisplayConfig;
  insConfig?: SideDisplayConfig;
  // Additional Details' all-column Text Search. Applied to the items table so the
  // shared search box reaches THIS tab too — it previously did not, so searching for
  // a reference here silently returned everything. Only the text is taken: the From/To
  // dates filter this period's doc_date, which a prior-period item does not have.
  searchText?: string;
  // Clears the shared search box from here, so a search that empties this table can be
  // undone without hunting back up the page for the input.
  onClearSearch?: () => void;
  // Re-run the reconciliation so a new override is reflected in the figures. Overrides
  // persist server-side, so the refresh reads them back rather than the client patching.
  onPairsChanged?: () => void;
  // Matching an item by hand happens in the Manual Match tab, where prior-period items
  // share the books with this period's rows (finance 2026-10-08). This opens it.
  onOpenManualMatch?: () => void;
}

type View = "open" | "matched" | "excluded" | "all";
// Which BOOK's carried list is on screen. Prior-period breaks exist on both ledgers
// (finance 2026-08-25) and the engine has always computed both, but this tab read only
// the insurer-side keys — so a CRI-side list was counted into its KPI card and appeared
// in no list at all (§6, counted-but-unlisted). Both are now selectable.
type Book = "insurer" | "cri";

// The carried row's own signature, carried through every projected frame as a hidden
// trailing column so the Decision control knows which item a rendered row is. Named
// with a leading "_" so allColumns' extras rule never renders it, and kept OUT of the
// text-search haystack (searching a signature would leave a row on screen with nothing
// visible in it matching what was typed).
const SIG_COL = "_unrec_sig";

// Evidence the engine records about WHAT settled an item. Blank by construction on
// every still-open row, so it is only ever shown on the settled view.
const EVIDENCE_COLS = [
  "unrec_matched_in", "unrec_cleared_by_tier", "unrec_cleared_by_ref",
  "unrec_cleared_by_date", "unrec_cleared_by_amount", "unrec_cleared_by_narration",
];
// The subset that still says something once the WHOLE clearing transaction is on the
// row beside it. Date and amount are that row's own cells and would simply be printed
// twice. Narration is NOT: on a MANUAL pair the engine writes the pairing's own label
// there (_apply_carried_manual's `how`), which exists on no row and would be lost.
const EVIDENCE_COLS_PAIRED = [
  "unrec_matched_in", "unrec_cleared_by_tier", "unrec_cleared_by_ref",
  "unrec_cleared_by_narration",
];

const EVIDENCE_LABELS: Record<string, string> = {
  unrec_matched_in: "Settled on",
  unrec_cleared_by_tier: "Matched by tier",
  unrec_cleared_by_ref: "Settled against (reference)",
  unrec_cleared_by_date: "Settled against (date)",
  unrec_cleared_by_amount: "Settled against (amount)",
  unrec_cleared_by_narration: "Settled against (narration)",
};

// One book's view of the block. Every figure this tab renders comes from here, so the
// two sides cannot drift apart or be half-wired: adding a book means adding a case,
// not threading `side ===` through the body.
interface BookView {
  rows: FrameRecords;
  sourceColumns?: string[];
  openTotal: number;
  openCount: number;
  matchedTotal: number;
  matchedCount: number;
  excludedCount: number;
  excludedTotal: number;
  precisionDamaged: number;
  closing: number;
  adjustedClosing: number;
  closingLabel: string;
}

function bookView(block: UnreconciledBlock, book: Book): BookView | null {
  if (book === "insurer") {
    return {
      rows: block.rows,
      sourceColumns: block.source_columns,
      openTotal: block.open_total,
      openCount: block.open_count,
      matchedTotal: block.matched_total,
      matchedCount: block.matched_count,
      excludedCount: block.excluded_count,
      excludedTotal: block.excluded_total,
      precisionDamaged: block.precision_damaged_count ?? 0,
      closing: block.insurer_closing,
      adjustedClosing: block.adjusted_closing,
      closingLabel: "Closing Balance (Insurer)",
    };
  }
  // cri_rows is null on an insurer-only upload — the signal that there is no CRI-side
  // population to show, rather than an empty one.
  if (!block.cri_rows) return null;
  return {
    rows: block.cri_rows,
    sourceColumns: block.cri_source_columns,
    openTotal: block.cri_open_total ?? 0,
    openCount: block.cri_open_count ?? 0,
    matchedTotal: block.cri_matched_total ?? 0,
    matchedCount: block.cri_matched_count ?? 0,
    excludedCount: block.cri_excluded_count ?? 0,
    excludedTotal: block.cri_excluded_total ?? 0,
    precisionDamaged: block.cri_precision_damaged_count ?? 0,
    closing: block.cri_closing ?? 0,
    adjustedClosing: block.cri_adjusted_closing ?? 0,
    closingLabel: "Closing Balance (CRI)",
  };
}

// One rendered block of settled items: the item on the left, the transaction that
// settled it on the right. Split by the BOOK the clearing row came from because the
// two ledgers have different columns — one grid could not honestly head both.
interface SettledSection {
  key: string;
  title: string;
  frame: FrameRecords;
  config: SideDisplayConfig;
  grouped: boolean;
  count: number;
}

// Prior-period unreconciled items. Every row the Adjusted Closing Balance is derived
// from is listed here — the still-open ones (which move the adjusted figure) and the
// ones this period settled (which deliberately do not). Nothing is counted upstream
// without appearing in this table.
export default function UnreconciledTab({ block, insurerName, criFrame, insurerFrame,
                                          criConfig, insConfig, searchText,
                                          onClearSearch, onPairsChanged,
                                          onOpenManualMatch }: Props) {
  const searchTrimmed = (searchText ?? "").trim();
  const [view, setView] = useState<View>("open");
  // Open on whichever book actually has a population, so a CRI-only upload does not
  // land on an empty insurer list.
  const hasCri = Boolean(block.cri_rows && block.cri_rows.rows.length > 0);
  const hasIns = block.rows.rows.length > 0;
  const [book, setBook] = useState<Book>(hasIns ? "insurer" : "cri");
  const side = bookView(block, book) ?? bookView(block, "insurer")!;

  const frame = side.rows;
  const matchedIdx = frame.columns.indexOf("unrec_matched");
  const excludedIdx = frame.columns.indexOf("unrec_excluded");
  const sigIdx = frame.columns.indexOf("unrec_sig");
  const rejectedIdx = frame.columns.indexOf("unrec_rejected");
  const tierIdx = frame.columns.indexOf("unrec_cleared_by_tier");

  // Overrides applied since this result was computed, so the list reacts immediately
  // instead of looking unchanged until the next reconcile. Keyed by row signature.
  const [pending, setPending] = useState<Record<string, "rejected" | "restored">>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  // The engine's verdict per item, keyed by the signature the Decision control carries.
  // Built once from the raw frame so the control never has to re-find its row.
  const stateBySig = useMemo(() => {
    const m = new Map<string, { matched: boolean; excluded: boolean; rejected: boolean;
                                hand: boolean }>();
    if (sigIdx < 0) return m;
    for (const r of frame.rows) {
      const sig = String(r[sigIdx] ?? "");
      if (!sig || m.has(sig)) continue;
      m.set(sig, {
        matched: matchedIdx >= 0 && Boolean(r[matchedIdx]),
        excluded: excludedIdx >= 0 && Boolean(r[excludedIdx]),
        rejected: rejectedIdx >= 0 && Boolean(r[rejectedIdx]),
        hand: tierIdx >= 0 && r[tierIdx] === "Manual",
      });
    }
    return m;
  }, [frame, sigIdx, matchedIdx, excludedIdx, rejectedIdx, tierIdx]);

  const isRejected = (sig: string): boolean => {
    if (sig && pending[sig]) return pending[sig] === "rejected";
    return stateBySig.get(sig)?.rejected ?? false;
  };

  // Reject = "this auto-clear is wrong, keep the item open". Restore = undo it. Both
  // persist per insurer against the row's durable CONTENT signature, so the decision
  // survives next month's upload however the file is re-ordered; neither touches the
  // reconciliation, so either can be reversed at any time.
  //
  // ONE CLICK, no reason dialog. The window.prompt this replaced blocked the whole page
  // on a modal, and cancelling it rejected the item anyway (`?? ""`) — so it never
  // guarded anything, it only cost a keystroke per row. The override itself is the
  // record, and it is reversible.
  async function setOverride(sig: string, reject: boolean) {
    if (!sig) return;
    setBusy(sig);
    setError("");
    try {
      if (reject) {
        await api.rejectUnreconciledClear(insurerName, sig, "", book);
        setPending((p) => ({ ...p, [sig]: "rejected" }));
      } else {
        await api.restoreUnreconciledClear(insurerName, sig, book);
        setPending((p) => ({ ...p, [sig]: "restored" }));
      }
      // RE-RUN, exactly as saving a carried pair does. The override is applied by the
      // engine (it reads the store in the finalize tail), so until the reconciliation
      // re-runs the item stays under "Settled this period" and the Adjusted Closing
      // still excludes it — the screen would contradict the decision just taken. This
      // is the ~0.4s finalize path, not a re-parse.
      onPairsChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  // Positions in `frame` that belong to the current view. One pass, shared by both the
  // plain table and the paired settled build, so the two can never disagree about
  // which items the view holds.
  const viewPositions = useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i < frame.rows.length; i++) {
      if (view !== "all" && matchedIdx >= 0) {
        const isExcluded = excludedIdx >= 0 && Boolean(frame.rows[i][excludedIdx]);
        const isMatched = Boolean(frame.rows[i][matchedIdx]);
        const keep = view === "excluded" ? isExcluded
          : view === "matched" ? (isMatched && !isExcluded)
          : (!isMatched && !isExcluded);
        if (!keep) continue;
      }
      out.push(i);
    }
    return out;
  }, [frame, view, matchedIdx, excludedIdx]);

  // The item's own columns: the ones finance's file carried (source_columns, computed
  // engine-side). The carried file is finance's list, not something the engine
  // reconciled, so the parse's derived twins and bookkeeping flags are workings, not
  // data — an 8-column file was rendering 48 columns here.
  //
  // Blank columns are pruned ONCE, over the WHOLE book — never per view. A column the
  // file populates on some other row is still one of its columns, and pruning per view
  // made the grid contradict itself: AL-ITTIHAD's "Balance" showed on All and vanished
  // on Still open (its only value sits on the total row). It also disagreed with the
  // Excel sheet, which has always pruned across every row. Columns with real data
  // anywhere now appear in every view, exactly as they do in the workbook.
  const itemCols = useMemo(
    () => populatedColumns(
      frame, (side.sourceColumns ?? frame.columns).filter((c) => frame.columns.includes(c))),
    [frame, side.sourceColumns],
  );

  // Slice by the engine's own flags — never re-derived here, so the table can't
  // disagree with the figures above it.
  const shown: FrameRecords = useMemo(() => {
    const wanted = [...itemCols, ...(view === "matched" ? EVIDENCE_COLS : [])];
    // Positions to keep, skipping any name the payload doesn't actually carry.
    const keepIdx = wanted
      .map((c) => frame.columns.indexOf(c))
      .filter((i) => i >= 0);
    const columns = keepIdx.map((i) => frame.columns[i]);
    const project = (r: FrameRecords["rows"][number]) => keepIdx.map((i) => r[i]);
    // Search the PROJECTED row against the PROJECTED columns, never the raw frame: a
    // hit on a hidden bookkeeping column (unrec_sig, the unrec_* flags) would leave a
    // row on screen with nothing in it matching what was typed.
    const needle = normaliseNeedle(searchText ?? "");
    const projected: FrameRecords = { columns, rows: [], indices: [], total_rows: 0 };

    const rows: FrameRecords["rows"] = [];
    const indices: FrameRecords["indices"] = [];
    for (const i of viewPositions) {
      const row = project(frame.rows[i]);
      if (needle && !rowMatchesText(projected, row, needle)) continue;
      // The signature rides behind the display cells, so it is never searched and
      // never rendered — only read by the Decision column.
      rows.push([...row, sigIdx >= 0 ? String(frame.rows[i][sigIdx] ?? "") : ""]);
      indices.push(frame.indices[i] ?? i);
    }
    return { columns: [...columns, SIG_COL], rows, indices, total_rows: rows.length };
  }, [frame, itemCols, view, viewPositions, sigIdx, searchText]);

  // The projected columns are passed as the CURATED list, not left to allColumns'
  // extras rule: that rule is written for engine frames and drops names beginning "_"
  // plus HIDDEN_EXTRA_COLS (role, signed_amount, recon_category...). Those are engine
  // concepts, but here a column is whatever finance TYPED in their file — a header
  // literally named "role" is their data and must never be silently hidden (§6).
  // Naming them curated shows exactly the projected set, in file order. SIG_COL is
  // deliberately absent: curated-but-unlisted plus a leading "_" is what keeps it off
  // screen through both of allColumns' passes.
  const tableConfig: SideDisplayConfig = useMemo(() => ({
    columns: shown.columns.filter((c) => c !== SIG_COL),
    renames: EVIDENCE_LABELS,
  }), [shown.columns]);

  // The settled view, paired. Each row carries the prior-period item AND the whole
  // transaction that settled it — the same side-by-side reading as the Matched tabs,
  // instead of a reference and a date the reader then has to go and look up. The
  // engine already names the clearing row (unrec_cleared_by_row is its frame index),
  // so this is a lookup, not a re-derivation.
  const settled: SettledSection[] | null = useMemo(() => {
    if (view !== "matched") return null;
    const clearedRowIdx = frame.columns.indexOf("unrec_cleared_by_row");
    const matchedInIdx = frame.columns.indexOf("unrec_matched_in");
    if (clearedRowIdx < 0 || matchedInIdx < 0) return null;

    // Only THIS PERIOD's two ledgers can be paired against. An item settled against
    // another prior-period item ("carried", a hand-made pair between two carried rows)
    // has no this-period counterpart to show, and both legs are already in this very
    // list — so it keeps the plain evidence table rather than a band that would repeat
    // the carried columns under a second identical header.
    const frameFor = (s: string): FrameRecords | undefined =>
      s === "insurer" ? insurerFrame : s === "cri" ? criFrame : undefined;
    const configFor = (s: string): SideDisplayConfig | undefined =>
      s === "insurer" ? insConfig : s === "cri" ? criConfig : undefined;
    const prefixFor = (s: string): string => (s === "cri" ? "cri" : "ins");

    // Resolve each settled item to its clearing row's POSITION, bucketed by the book
    // that row came from. A row we cannot resolve (no counterpart recorded, or a frame
    // this response did not carry) still has to be listed — with its evidence columns,
    // exactly as before — or it would be counted and unlisted (§6).
    const buckets = new Map<string, { pos: number; partner: number | null }[]>();
    for (const i of viewPositions) {
      const s = String(frame.rows[i][matchedInIdx] ?? "");
      const label = String(frame.rows[i][clearedRowIdx] ?? "");
      const target = label ? frameFor(s) : undefined;
      const pp = target ? frameIndexMap(target).get(label) : undefined;
      const key = pp === undefined ? (s === "carried" ? "carried" : "none") : s;
      const bucket = buckets.get(key) ?? [];
      bucket.push({ pos: i, partner: pp === undefined ? null : pp });
      buckets.set(key, bucket);
    }

    const needle = normaliseNeedle(searchText ?? "");
    const out: SettledSection[] = [];
    for (const [key, members] of buckets) {
      const paired = key === "insurer" || key === "cri";
      const evidence = (paired ? EVIDENCE_COLS_PAIRED : EVIDENCE_COLS)
        .filter((c) => frame.columns.includes(c));
      const leftIdx = [...itemCols, ...evidence].map((c) => frame.columns.indexOf(c));

      if (!paired) {
        // No this-period counterpart to show — the plain evidence table, unchanged.
        const columns = leftIdx.map((i) => frame.columns[i]);
        const projected: FrameRecords = { columns, rows: [], indices: [], total_rows: 0 };
        const rows: FrameRecords["rows"] = [];
        const indices: FrameRecords["indices"] = [];
        for (const m of members) {
          const row = leftIdx.map((i) => frame.rows[m.pos][i]);
          if (needle && !rowMatchesText(projected, row, needle)) continue;
          rows.push([...row, sigIdx >= 0 ? String(frame.rows[m.pos][sigIdx] ?? "") : ""]);
          indices.push(frame.indices[m.pos] ?? m.pos);
        }
        if (!rows.length) continue;
        out.push({
          key,
          title: key === "carried"
            ? `Settled against another prior-period item (${rows.length.toLocaleString()})`
            : `Settled — clearing transaction not in this view (${rows.length.toLocaleString()})`,
          frame: { columns: [...columns, SIG_COL], rows, indices, total_rows: rows.length },
          config: { columns, renames: EVIDENCE_LABELS },
          grouped: false,
          count: rows.length,
        });
        continue;
      }

      const target = frameFor(key)!;
      const prefix = prefixFor(key);
      // Blank-column suppression is scoped to the clearing rows actually rendered, so
      // a 69k-row ledger is not scanned to decide this handful of rows' columns, and
      // only the columns those rows populate are shown.
      const rightCols = allColumns(target, configFor(key),
                                   members.map((m) => m.partner!).filter((p) => p >= 0));
      const itemKeys = itemCols.map((c) => "carried::" + c);
      const evKeys = evidence.map((c) => "meta::" + c);
      const rightKeys = rightCols.map((c) => `${prefix}::${c.key}`);
      const columns = [...itemKeys, ...evKeys, ...rightKeys];
      const projected: FrameRecords = { columns, rows: [], indices: [], total_rows: 0 };

      const rows: FrameRecords["rows"] = [];
      const indices: FrameRecords["indices"] = [];
      for (const m of members) {
        const src = frame.rows[m.pos];
        const partnerRow = m.partner === null ? null : target.rows[m.partner];
        const row: FrameRecords["rows"][number] = [
          ...leftIdx.map((i) => src[i]),
          ...rightCols.map((c) => (partnerRow ? partnerRow[c.index] : null)),
        ];
        if (needle && !rowMatchesText(projected, row, needle)) continue;
        rows.push([...row, sigIdx >= 0 ? String(src[sigIdx] ?? "") : ""]);
        indices.push(frame.indices[m.pos] ?? m.pos);
      }
      if (!rows.length) continue;

      const bookName = key === "cri" ? "the CRI ledger"
        : `the ${insurerName || "insurer"} ledger`;
      out.push({
        key,
        title: `Settled against ${bookName} (${rows.length.toLocaleString()})`,
        frame: { columns: [...columns, SIG_COL], rows, indices, total_rows: rows.length },
        config: {
          columns,
          // Bare labels: the side is carried by the spanning band above the header,
          // not repeated into every column name.
          renames: Object.fromEntries([
            ...itemCols.map((c) => ["carried::" + c, c]),
            ...evidence.map((c) => ["meta::" + c, EVIDENCE_LABELS[c] ?? c]),
            ...rightCols.map((c) => [`${prefix}::${c.key}`, c.label]),
          ]),
        },
        grouped: true,
        count: rows.length,
      });
    }
    // Insurer, then CRI, then the odd cases — a stable order, so a re-render never
    // reshuffles the sections under the reader.
    const rank = (k: string) => (k === "insurer" ? 0 : k === "cri" ? 1 : k === "carried" ? 2 : 3);
    return out.sort((a, b) => rank(a.key) - rank(b.key));
  }, [view, frame, viewPositions, itemCols, insurerFrame, criFrame, insConfig, criConfig,
      insurerName, sigIdx, searchText]);

  const total = side.openCount + side.matchedCount + side.excludedCount;

  // How many items this view holds BEFORE the search, so "3 of 24 match" states the
  // view's own population rather than the whole file's.
  const viewTotal = viewPositions.length;
  // What the search actually left on screen, across every section the view renders.
  const shownCount = settled
    ? settled.reduce((n, s) => n + s.count, 0)
    : shown.rows.length;

  // The Decision control, rendered per row in a sticky column that stays visible on
  // sideways scroll — the decision belongs ON the transaction, not in a strip of
  // buttons above it that names rows by reference. Nothing here mutates the
  // reconciliation, so every action is reversible.
  const sigOf = (row: unknown[], columns: string[]): string => {
    const i = columns.indexOf(SIG_COL);
    return i >= 0 ? String(row[i] ?? "") : "";
  };
  const decisionCell = (sig: string) => {
    const st = sig ? stateBySig.get(sig) : undefined;
    if (!st || st.excluded) return null;
    const rejected = isRejected(sig);
    // A still-open item that was never auto-cleared has no clear to reject.
    if (!st.matched && !rejected) {
      return <span className="muted" title="Not auto-cleared — nothing to undo">—</span>;
    }
    // Settled by a hand pair, which outranks a rejection: Reject would change nothing.
    if (st.hand && !rejected) {
      return <span className="muted" title="Settled by a hand match: undo it with Unmatch on the Manual Match tab">Hand match</span>;
    }
    return (
      <button className="btn btn-sm" disabled={busy === sig}
        title={rejected
          ? "Let the matcher clear this item again"
          : "Keep this item open — the decision is remembered for this insurer"}
        onClick={() => setOverride(sig, !rejected)}>
        {busy === sig ? "…" : rejected ? "Restore" : "Reject"}
      </button>
    );
  };

  // FINANCE'S NOTE on an item (math-inert). Shows the note the run carries — theirs, else
  // the engine's auto-annotation — and edits only theirs, which outranks the auto one.
  // Saved per insurer against the item's signature, so it re-applies every upload and
  // reaches the Excel Group tabs' note column after the re-run.
  const noteIdx = frame.columns.indexOf("manual_note");
  const noteBySig = useMemo(() => {
    const m = new Map<string, string>();
    if (sigIdx < 0 || noteIdx < 0) return m;
    for (const r of frame.rows) {
      const n = String(r[noteIdx] ?? "").trim();
      if (n) m.set(String(r[sigIdx] ?? ""), n);
    }
    return m;
  }, [frame, sigIdx, noteIdx]);
  const [userNotes, setUserNotes] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let stale = false;
    api.carriedNotes(insurerName)
      .then((r) => { if (!stale) setUserNotes(new Map(r.notes.map((n) => [`${n.list}|${n.sig}`, n.note]))); })
      .catch(() => {});
    return () => { stale = true; };
  }, [insurerName, block]);
  const [noteEdit, setNoteEdit] = useState<{ sig: string; draft: string } | null>(null);
  async function saveNote(sig: string, text: string) {
    setBusy(sig);
    setError("");
    try {
      const r = await api.setCarriedNote(insurerName, book, sig, text);
      setUserNotes(new Map(r.notes.map((n) => [`${n.list}|${n.sig}`, n.note])));
      setNoteEdit(null);
      onPairsChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }
  const noteCell = (sig: string) => {
    if (!sig || stateBySig.get(sig)?.excluded) return null;
    if (noteEdit?.sig === sig) {
      return (
        <span className="unrec-note-edit">
          <input className="input" autoFocus value={noteEdit.draft} aria-label="Note on this item"
            placeholder={noteBySig.get(sig) ?? "Note on this item"}
            onChange={(e) => setNoteEdit({ sig, draft: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveNote(sig, noteEdit.draft);
              if (e.key === "Escape") setNoteEdit(null);
            }} />
          <button className="btn btn-sm" disabled={busy === sig}
            onClick={() => saveNote(sig, noteEdit.draft)}>Save</button>
          <button className="btn btn-sm" onClick={() => setNoteEdit(null)}>Cancel</button>
        </span>
      );
    }
    const shownNote = noteBySig.get(sig) ?? "";
    const own = userNotes.get(`${book}|${sig}`) ?? "";
    return (
      <span className="unrec-note">
        <span className={own ? "unrec-note-text" : "unrec-note-text muted"} title={shownNote}>
          {shownNote || "—"}
        </span>
        <button className="btn btn-sm" title={own ? "Edit your note (blank removes it)" : "Add a note"}
          onClick={() => setNoteEdit({ sig, draft: own })}>
          {own ? "Edit" : "Note"}
        </button>
      </span>
    );
  };
  const actionCell = (columns: string[]) => (row: unknown[]) => {
    const sig = sigOf(row, columns);
    return (
      <span className="unrec-action-cell">
        {hasDecisions && decisionCell(sig)}
        {noteCell(sig)}
      </span>
    );
  };
  // THE COLUMN EARNS ITS PLACE PER VIEW. On "Still open" with nothing rejected there is
  // no clear to reject and nothing to restore, so every cell was a dash — a column of
  // furniture the reader has to scroll past and interpret. Scoped to the view's own
  // population (not the search-filtered slice) so typing in the search box cannot make
  // a control column appear and disappear under the cursor.
  const hasDecisions = useMemo(() => {
    if (sigIdx < 0) return false;
    return viewPositions.some((i) => {
      const sig = String(frame.rows[i][sigIdx] ?? "");
      const st = stateBySig.get(sig);
      if (!st || st.excluded) return false;
      return st.matched || isRejected(sig);
    });
  }, [viewPositions, frame, sigIdx, stateBySig, pending]);

  // The Note is every item's, so the column is always there; the Decision joins it only
  // where the view has a clear to reject or restore.
  const actionHeader = hasDecisions ? "Decision · Note" : "Note";
  const rowAction = sigIdx < 0 ? undefined
    : { header: actionHeader, render: actionCell(shown.columns) };

  const stripRef = useScrollEdges();

  return (
    <div>
      <p className="muted no-top">
        Items carried forward from earlier periods, checked against this period on
        reference plus amount.
        <InfoTip text="Settled this period = the item's reference and amount were found on a transaction that is itself reconciled in this period. An item is NOT settled by its own still-open row (a cumulative statement re-lists it every month), and a policy number alone never settles it - many transactions share one policy. Still open = everything else; these are what the Adjusted Closing Balance allows for. The Closing Balance and Difference are unaffected either way." />
      </p>

      {/* Book scope. Sits ABOVE the figures because it is what the figures are OF —
          a nested tab strip made it read as a third level of navigation rather than
          as the selector that changes the three cards below it. Shown only when both
          books carry a population, so a single-sided upload gets no needless control. */}
      {hasIns && hasCri && (
        <div className="scope-row">
          <span className="muted" id="carried-book-label">Showing</span>
          <div className="segmented" role="group" aria-labelledby="carried-book-label">
            {(["insurer", "cri"] as const).map((b) => {
              const v = bookView(block, b);
              const n = v ? v.openCount + v.matchedCount + v.excludedCount : 0;
              return (
                <button key={b} className={`seg-opt ${book === b ? "active" : ""}`}
                  aria-pressed={book === b}
                  onClick={() => { setBook(b); setView("open"); }}>
                  {b === "insurer" ? "Insurer list" : "CRI list"} ({n})
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="row row-gap">
        <div className="col kpi-card">
          <div className="kpi-label">Still open</div>
          <div className="kpi-value">{fmtAed(side.openTotal)}</div>
          <div className="muted stack-sm">
            {side.openCount.toLocaleString()} of {total.toLocaleString()} items
          </div>
        </div>
        <div className="col kpi-card">
          <div className="kpi-label">Settled this period</div>
          <div className="kpi-value">{fmtAed(side.matchedTotal)}</div>
          <div className="muted stack-sm">
            {side.matchedCount.toLocaleString()} of {total.toLocaleString()} items
          </div>
        </div>
        <div className="col kpi-card">
          <div className="kpi-label">Adjusted Closing Balance</div>
          <div className="kpi-value">{fmtAed(side.adjustedClosing)}</div>
          <div className="muted stack-sm">
            closing {fmtAed(side.closing)} + still open
          </div>
        </div>
      </div>

      {/* ONE strip: the four item views are filters over the same list. Matching them
          by hand is in the Manual Match tab, beside this period's rows. */}
      <div className="tabstrip gap-bottom" role="tablist" aria-label="Prior-period items" ref={stripRef}>
        {(["open", "matched", "excluded", "all"] as const)
          .filter((v) => v !== "excluded" || side.excludedCount > 0)
          .map((v) => (
          <button key={v} role="tab" aria-selected={view === v}
            className={`tab ${view === v ? "active" : ""}`}
            onClick={() => setView(v)}>
            {v === "open" ? `Still open (${side.openCount})`
              : v === "matched" ? `Settled this period (${side.matchedCount})`
              : v === "excluded" ? `Not an item (${side.excludedCount})`
              : `All (${total})`}
          </button>
        ))}
      </div>

      {(side.precisionDamaged ?? 0) > 0 && (
        <p className="muted no-top">
          <strong>{side.precisionDamaged!.toLocaleString()} item
          {side.precisionDamaged === 1 ? "'s" : "s'"} reference</strong> arrived
          as a number too long for Excel to store exactly, so its final digits were lost
          in the file before it was read. {side.precisionDamaged === 1
            ? "That item cannot be checked against this period and is shown as still open"
            : "Those items cannot be checked against this period and are shown as still open"}.
          Re-save the reference column as <em>text</em> and upload again to have
          {side.precisionDamaged === 1 ? " it" : " them"} checked.
        </p>
      )}

      {side.excludedCount > 0 && (
        <p className="muted no-top">
          {side.excludedCount.toLocaleString()} row
          {side.excludedCount === 1 ? " was" : "s were"} read as a heading or total line
          ({fmtAed(side.excludedTotal)}) rather than an item, so
          {side.excludedCount === 1 ? " it is" : " they are"} excluded from the
          adjustment. Check the &ldquo;Not an item&rdquo; view if
          {side.excludedCount === 1 ? " it" : " any"} should have counted.
        </p>
      )}

      {/* --danger was never defined, so this silently fell back to a hardcoded #b00
          that stayed dark-red on the dark theme. --risk is the real token. */}
      {error && <p className="muted text-risk" role="alert">{error}</p>}

      {hasDecisions && (
        <p className="muted note-tight">
          The <strong>Decision</strong> column holds each item&rsquo;s override:
          reject a clear you disagree with and the item stays open; restore one to let
          the matcher clear it again. The decision is remembered for this insurer and
          can be undone at any time.
        </p>
      )}

      {/* Matching by hand lives on the main Manual Match tab, where these items share
          the books with this period's rows, so finance uses one interaction for both. */}
      {view === "open" && sigIdx >= 0 && (criFrame || insurerFrame)
        && side.openCount > 0 && onOpenManualMatch && (
        <p className="muted note-block">
          Settled one of these by hand?{" "}
          <button className="btn btn-chip" onClick={onOpenManualMatch}>
            Match it on the Manual Match tab
          </button>
        </p>
      )}

      {/* A search that hides every row must say so. "No items in this view" on a
          non-empty tab reads as "this insurer has none", which is the wrong
          conclusion — and leaves no clue that a filter is doing it. */}
      {searchTrimmed && (
        <p className="muted note-block-sm" role="status">
          {shownCount === 0
            ? `No prior-period item in this view matches "${searchTrimmed}".`
            : `${shownCount.toLocaleString()} of ${viewTotal.toLocaleString()} `
              + `item${viewTotal === 1 ? "" : "s"} in this view match "${searchTrimmed}".`}
          <button className="btn btn-chip badge-inline"
            onClick={onClearSearch}>Clear search</button>
        </p>
      )}

      {shownCount === 0
        ? <p className="muted">
            {searchTrimmed ? "No items match your search." : "No items in this view."}
          </p>
        : settled
          ? settled.map((sec) => (
              <div key={sec.key}>
                {settled.length > 1 && <h4 className="subhead">{sec.title}</h4>}
                <DataTable frame={sec.frame} pageSize={50} config={sec.config}
                  grouped={sec.grouped}
                  /* The paired scope: these keys (carried::/meta::/ins::) are a
                     different vocabulary from the single-book grids, so pinning here
                     must not reorder them. */
                  pinSide={sec.grouped ? "paired" : book} insurerName={insurerName}
                  keepCuratedColumns keyColumnsFirst={!sec.grouped}
                  rowAction={sigIdx < 0 ? undefined
                    : { header: actionHeader, render: actionCell(sec.frame.columns) }}
                  title={sec.title} />
              </div>
            ))
          : <DataTable frame={shown} pageSize={50} config={tableConfig}
              /* Pin scope follows the BOOK on screen: the two lists have different
                 columns, so sharing one scope would apply the insurer list's pins to
                 the CRI grid (and CRI pins are shared across insurers, insurer pins
                 are per-insurer — see useColumnPins). */
              pinSide={book} insurerName={insurerName}
              /* The column set was decided over the whole book above; without this the
                 grid would prune it a second time against the rows in THIS view. */
              keepCuratedColumns keyColumnsFirst
              rowAction={rowAction}
              title="Prior-period unreconciled items" />}
    </div>
  );
}
